import { useMemo, useState } from "react";
import type { ItemType, Product } from "./api";
import { formatMoney } from "./api";
import { productName } from "./productName";
import { Modal } from "./ui";

/**
 * A list pasted into a purchase (D, 14 October 2026: "In 1 transaction I want to list down
 * these 3, Singles, Slabs, Sealed Products", 63 lines kept in a spreadsheet). Each row
 * becomes a line. A row is read from the spreadsheet's columns: its name, then quantity,
 * unit price and total, as many of those as it has. A name already on the product list is
 * that product; any other becomes a new product of the kind the list was pasted as.
 */

export type ListKind = "singles" | "sealed" | "slabs";

export interface PastedRow {
  readonly name: string;
  readonly quantity: number;
  /** In dollars, as typed; empty when the list gave names only, to be priced on the line. */
  readonly total: string;
  readonly productId: string | null;
  readonly kind: ListKind;
  readonly itemType: ItemType;
  readonly language: string | null;
  readonly grader?: string;
  readonly grade?: string;
}

const LANGUAGE = /^(EN|JP|CN|KR)\s+/i;

/** "JP McDonald's Promo Wooper" is a Japanese card called "McDonald's Promo Wooper". */
export function languageOf(name: string): { language: string | null; rest: string } {
  const found = LANGUAGE.exec(name);
  return found ? { language: found[1]!.toUpperCase(), rest: name.slice(found[0].length).trim() } : { language: null, rest: name.trim() };
}

/** What sort of sealed product a name sounds like. */
export function sealedType(name: string): ItemType {
  if (/\bcase\b/i.test(name)) return "case";
  if (/\bdisplay\b/i.test(name)) return "display";
  if (/booster box|\bbox\b/i.test(name) && !/trainer/i.test(name)) return "box";
  if (/elite trainer|\betb\b/i.test(name)) return "etb";
  if (/bundle/i.test(name)) return "bundle";
  if (/\btin\b/i.test(name)) return "tin";
  if (/collection|premium|gift/i.test(name)) return "collection";
  if (/pack|pcj|sleeve|blister|booster/i.test(name)) return "pack";
  return "other";
}

const NUMBER = /^(?:S?\$)?\s*-?[\d,]+(?:\.\d+)?$/i;
const amount = (cell: string): number => Number(cell.replace(/[^\d.-]/g, ""));
const key = (name: string): string => name.toLowerCase().replace(/\s+/g, " ").trim();

/** The rows of a pasted list, and how many rows were left out because they had nothing to buy. */
export function readList(text: string, kind: ListKind, products: readonly Product[]): { rows: PastedRow[]; skipped: number } {
  const known = new Map<string, Product>();
  for (const product of products) {
    known.set(key(product.name), product);
    known.set(key(productName(product)), product);
  }
  const rows: PastedRow[] = [];
  let skipped = 0;
  for (const raw of text.split(/\r?\n/)) {
    if (!raw.trim()) continue;
    let cells = (raw.includes("\t") ? raw.split("\t") : raw.split(/\s{2,}/)).map((cell) => cell.trim()).filter(Boolean);
    // A line typed as "140 1. JP Wooper": the amount, then the numbered name. Only with the
    // number after it, so a name that starts with one, "151 Booster Bundle", stays a name.
    if (cells.length === 1) {
      const typed = /^(?:S?\$)?([\d,]+(?:\.\d+)?)\s+(\d+\.\s+.+)$/i.exec(cells[0]!);
      if (typed) cells = [typed[2]!, typed[1]!];
    }
    // The spreadsheet's own row number, ahead of the name.
    if (cells.length > 1 && /^\d+\.?$/.test(cells[0]!) && !NUMBER.test(cells[1]!)) cells = cells.slice(1);
    const words = cells.filter((cell) => !NUMBER.test(cell)).map((cell) => cell.replace(/^\d+\.\s+/, "").trim());
    const numbers = cells.filter((cell) => NUMBER.test(cell)).map(amount);
    // The header, and the total underneath.
    if (words.length === 0) continue;
    if (words.every((word) => /^(total|subtotal|grand total|item name|items?|#|qty|quantity|unit price|price|total price|selling price|amount)$/i.test(word))) continue;
    // Of the names on a row, the fullest: "JP McDonald's Promo Wooper #022/P" over "Wooper 22".
    const name = words.reduce((longest, word) => (word.length > longest.length ? word : longest), "");
    // A list of names only (D, 15 October 2026: it added nothing) gives one of each, the
    // price to be put on the line.
    let quantity = 1;
    let total: number | null = numbers[0] ?? null;
    if (numbers.length >= 3) [quantity, , total] = numbers as [number, number, number];
    else if (numbers.length === 2) [quantity, total] = numbers as [number, number];
    if (!(quantity > 0)) {
      skipped++;
      continue;
    }
    const product = known.get(key(name));
    const { language } = languageOf(name);
    if (kind === "slabs") {
      const graded = /\b(PSA|BGS|CGC|SGC|TAG|ACE)\s*(\d+(?:\.\d)?)\s*$/i.exec(name);
      // A slab is one card; three on a row are three lines at a third each.
      const each = total === null ? null : Math.round((total * 100) / quantity) / 100;
      for (let one = 0; one < quantity; one++) {
        rows.push({
          name,
          quantity: 1,
          total: total === null || each === null ? "" : (one === quantity - 1 ? total - each * (quantity - 1) : each).toFixed(2),
          productId: null,
          kind,
          itemType: "single",
          language,
          ...(graded ? { grader: graded[1]!.toUpperCase(), grade: graded[2]! } : {}),
        });
      }
      continue;
    }
    rows.push({
      name,
      quantity,
      total: total === null ? "" : total.toFixed(2),
      productId: product?.id ?? null,
      kind,
      itemType: product?.itemType ?? (kind === "singles" ? "single" : sealedType(name)),
      language: product ? product.language : language,
    });
  }
  return { rows, skipped };
}

const KINDS: readonly { value: ListKind; label: string; hint: string }[] = [
  { value: "singles", label: "Singles", hint: "Each new name becomes a single card on the Singles tab." },
  { value: "sealed", label: "Sealed products", hint: "Each new name becomes a sealed product. Its type is guessed from the name and can be changed on the line." },
  { value: "slabs", label: "Graded slabs", hint: "Each row becomes a graded card. Add the certificate numbers later, on the line or on Inventory." },
];

export function PasteList({
  products,
  onClose,
  onAdd,
}: {
  readonly products: readonly Product[];
  readonly onClose: () => void;
  readonly onAdd: (rows: readonly PastedRow[]) => void;
}): React.ReactElement {
  const [kind, setKind] = useState<ListKind>("singles");
  // Each kind keeps its own list (D, 15 October 2026: pasting under Singles showed under all three).
  const [texts, setTexts] = useState<Record<ListKind, string>>({ singles: "", sealed: "", slabs: "" });
  const text = texts[kind];
  const read = useMemo(() => {
    const each = (["singles", "sealed", "slabs"] as const).map((one) => readList(texts[one], one, products));
    return { rows: each.flatMap((one) => one.rows), skipped: each.reduce((sum, one) => sum + one.skipped, 0) };
  }, [texts, products]);
  const counts = { singles: 0, sealed: 0, slabs: 0 };
  for (const row of read.rows) counts[row.kind]++;
  const unpriced = read.rows.filter((row) => !row.total).length;
  const totalCents = read.rows.reduce((sum, row) => sum + Math.round(Number(row.total || 0) * 100), 0);
  const here = read.rows.filter((row) => row.kind === kind);
  const fresh = read.rows.filter((row) => row.kind !== "slabs" && !row.productId).length;
  return (
    <Modal
      open
      size="lg"
      title="Paste a list"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ti-btn ti-btn-primary-full" disabled={read.rows.length === 0} onClick={() => onAdd(read.rows)}>
            Add {read.rows.length} {read.rows.length === 1 ? "line" : "lines"}
          </button>
        </>
      }
    >
      <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
        Each tab keeps its own list: paste the singles under Singles, the sealed products under Sealed products and the slabs under
        Graded slabs, then add them all at once. Columns are read as item name, quantity, unit price and total. A list of names only
        is fine: each comes in as one, and the quantity and price are filled in on the line.
      </p>
      <div className="flex flex-wrap gap-2 mb-2" role="radiogroup" aria-label="What the list is">
        {KINDS.map((one) => (
          <button
            key={one.value}
            type="button"
            role="radio"
            aria-checked={kind === one.value}
            className={`ti-btn !mb-0 ${kind === one.value ? "ti-btn-primary-full" : "ti-btn-light"}`}
            onClick={() => setKind(one.value)}
          >
            {one.label}
            {counts[one.value] ? ` (${counts[one.value]})` : ""}
          </button>
        ))}
      </div>
      <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-2">{KINDS.find((one) => one.value === kind)?.hint}</p>
      <textarea
        rows={8}
        className="form-control font-mono !text-[0.75rem]"
        aria-label="The pasted list"
        placeholder={"1\tCrown Zenith Packs\t20\t33\t660\n2\tPerfect Order Packs\t15\t7\t105"}
        value={text}
        onChange={(event) => setTexts((was) => ({ ...was, [kind]: event.target.value }))}
      />
      {read.rows.length > 0 ? (
        <div className="mt-3">
          <p className="mb-1 text-[0.813rem]">
            In all: {counts.singles} singles, {counts.sealed} sealed, {counts.slabs} slabs, S$ {formatMoney(totalCents)}
            {fresh > 0 ? `. ${fresh} not on the product list yet, made when the purchase is saved` : ""}
            {unpriced > 0 ? `. ${unpriced} with no price yet, to fill in on the line` : ""}
            {read.skipped > 0 ? `. ${read.skipped} with quantity 0 left out` : ""}.
          </p>
          <div className="max-h-[14rem] overflow-y-auto border border-defaultborder rounded-md">
            <table className="w-full text-[0.75rem]">
              <tbody>
                {here.map((row, index) => (
                  <tr key={index} className="border-b border-defaultborder">
                    <td className="px-2 py-1">{row.name}</td>
                    <td className="px-2 py-1 text-[#8c9097] whitespace-nowrap">
                      {row.kind === "slabs" ? (row.grader ? `${row.grader} ${row.grade}` : "Graded card") : row.productId ? "On the list" : "New"}
                    </td>
                    <td className="px-2 py-1 text-end tabular-nums">{row.quantity}</td>
                    <td className="px-2 py-1 text-end tabular-nums">{row.total || <span className="text-warning">No price</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
